import { CartLinesUpdateEvent } from '@shopify/events';

class NanoCartPersonalisation extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.sourceKey = new URL(location.href).searchParams.get('nano_personalise');
    if (!this.sourceKey) return;
    const url = new URL(location.href);
    let attempt = url.searchParams.get('nano_personalise_attempt');
    if (!attempt) {
      attempt = crypto.randomUUID();
      url.searchParams.set('nano_personalise_attempt', attempt);
      history.replaceState(history.state, '', url);
    }
    this.operationId = `${this.sourceKey}/${attempt}`;
    this.form = this.closest('form');
    this.copy = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    this.status = this.querySelector('[data-upgrade-status]');
    this.button = this.form.querySelector('[type="submit"]');
    this.quantity = this.form.querySelector('[name="quantity"]');
    this.hidden = false;
    this.quantity.readOnly = true;
    this.setLabel(this.copy.update);
    this.ready = this.prepare();
    this.querySelector('[data-upgrade-bag]').addEventListener('click', (event) => {
      const drawer = document.querySelector('#cart-drawer');
      if (drawer && this.done) { event.preventDefault(); drawer.open(); }
    });
  }

  setLabel(text) {
    const label = this.button?.querySelector(':scope > span:not([data-price]):not(.add-to-cart__added)');
    if (label) label.textContent = text;
    const sticky = this.closest('nano-product-draft2')?.querySelector('.sticky-purchase');
    if (sticky) {
      sticky.dataset.addLabel = text;
      if (this.done) sticky.querySelector('.sticky-button').firstElementChild.textContent = text;
    }
  }

  async request(endpoint, body) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}${endpoint}`, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const result = await response.json();
    if (!response.ok || result.status >= 400) throw new Error('request');
    return result;
  }

  async prepare() {
    this.button.disabled = true;
    this.status.textContent = this.copy.loading;
    try {
      const cart = await this.request('cart.js');
      const source = cart.items.find((item) => item.key === this.sourceKey);
      if (!source || String(source.variant_id) !== this.dataset.standardVariant || source.selling_plan_allocation) {
        const existing = cart.items.find((item) => String(item.variant_id) === this.dataset.personalVariant && item.properties?._nano_personalisation_source === this.operationId);
        if (!source && existing) { this.done = true; this.status.textContent = this.copy.success; return; }
        throw new Error('changed');
      }
      this.source = source;
      this.quantity.value = source.quantity;
      this.status.textContent = this.copy.intro;
      this.button.disabled = false;
    } catch { this.status.textContent = this.copy.changed; }
  }

  async submit() {
    if (this.busy || this.done) return;
    this.busy = true;
    await this.ready;
    if (!this.source || !this.form.reportValidity()) { this.busy = false; return; }
    const fields = [...this.form.querySelectorAll('[data-nano-engraving-input]')];
    const confirmation = this.form.querySelector('[data-nano-engraving-confirm]');
    if (!confirmation?.checked || fields.some((field) => !field.value.trim() || field.value.length > field.maxLength)) { this.busy = false; return; }
    const targetId = this.form.querySelector('[name="id"]').value;
    if (targetId !== this.dataset.personalVariant || !Number(targetId)) { this.status.textContent = this.copy.changed; this.busy = false; return; }
    const properties = Object.fromEntries(fields.map((field) => [field.name.slice(11, -1), field.value]));
    this.button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.status.textContent = this.copy.saving;
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId))].slice(0, 5).join(',');
    try {
      const { personaliseCartLine } = await import(this.dataset.transactionUrl);
      const run = () => personaliseCartLine({ request: this.request.bind(this), source: this.source, operationId: this.operationId, targetId, properties, sections });
      const cart = navigator.locks ? await navigator.locks.request('nano-cart-personalisation', run) : await run();
      this.done = true;
      this.status.textContent = this.copy.success;
      this.setLabel(this.copy.updated);
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'change', context: 'product', lines: [], promise: deferred.promise }));
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections: cart.sections, didError: false, source: 'nano-cart-personalisation' } });
      document.querySelector('#cart-drawer')?.open();
    } catch (error) {
      this.status.textContent = error.message === 'changed' ? this.copy.changed : this.copy.error;
      this.button.disabled = false;
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('nano-cart-personalisation')) customElements.define('nano-cart-personalisation', NanoCartPersonalisation);
